import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ListChecks } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { NoteItem } from "./NoteItem";
import { InboxThread } from "./InboxThread";

const FLASH = "focus-glow p-2 -m-2";

function TaskCard({ m, me, flash, onChange }) {
  return (
    <div id={`thread-task-${m.id}`} data-testid={`thread-task-${m.id}`}
      className={`rounded-2xl ${flash ? FLASH : ""}`}>
      <div className="mb-1 px-1 flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-bold text-[#1E5FEB]">
        <ListChecks size={12} weight="bold" />Task · {m.from_name} → {m.to_name}
      </div>
      <InboxThread m={m} me={me} onChange={onChange} initialOpen={flash} showSentStatus={me === m.from_id} />
    </div>
  );
}

export function TicketNotesThread({ ticket, notes, user, staff, onEditNote, onDeleteNote, onReload, refreshKey }) {
  const [params, setParams] = useSearchParams();
  const retried = useRef(null);
  const [tasks, setTasks] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [flashId, setFlashId] = useState(null);
  const target = params.get("note") || params.get("task");

  const load = useCallback(() => Promise.all([
    api.get(`/repair-tickets/${ticket.id}/tasks`).then((r) => setTasks(r.data)).catch(() => {}),
    api.get(`/repair-tickets/${ticket.id}/my-mentions`).then((r) => setMentions(r.data)).catch(() => {}),
  ]), [ticket.id]);
  useEffect(() => { load(); }, [load, ticket.updated_at, notes.length, refreshKey]);

  useEffect(() => {
    if (!target) return;
    const find = () => document.getElementById(`note-item-${target}`) || document.getElementById(`thread-task-${target}`);
    const clear = () => { const next = new URLSearchParams(window.location.search); next.delete("note"); next.delete("task"); setParams(next, { replace: true }); };
    const el = find();
    if (el) {
      const center = () => (find() || el).scrollIntoView({ behavior: "smooth", block: "center" });
      center();
      [450, 1100].forEach((ms) => setTimeout(center, ms));
      setFlashId(target);
      clear();
    } else if (retried.current !== target) {
      retried.current = target;
      Promise.all([onReload?.(), load()]).then(() => setTimeout(() => { if (!find()) clear(); }, 300));
    }
  }, [target, tasks.length, notes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!flashId) return;
    const tm = setTimeout(() => setFlashId(null), 4000);
    return () => clearTimeout(tm);
  }, [flashId]);

  const markDone = async (n, done) => {
    await api.post(`/notifications/${n.id}/done`, { done });
    load();
  };

  const items = [
    ...notes.map((n) => ({ type: "note", at: n.created_at, n })),
    ...tasks.map((m) => ({ type: "task", at: m.created_at, m })),
  ].sort((a, b) => (a.at || "").localeCompare(b.at || ""));

  return (
    <div className="space-y-4 mb-5" data-testid="notes-thread">
      {items.length === 0 && <div className="text-[13px] text-slate-500">No notes yet. Start the conversation below.</div>}
      {items.map((it) => it.type === "task"
        ? <TaskCard key={`t-${it.m.id}`} m={it.m} me={user?.id} flash={flashId === it.m.id} onChange={load} />
        : <NoteItem key={it.n.id} n={it.n} mine={it.n.author === user?.name} staff={staff}
                    flash={flashId === it.n.id} mention={mentions.find((x) => x.note_id === it.n.id)} onMentionDone={markDone}
                    onEdit={(text, ms) => onEditNote(it.n, text, ms)} onDelete={() => onDeleteNote(it.n)} />)}
    </div>
  );
}
